import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isFreshStartMode } from "@/utils/freshStart";

export type SupportedLanguage = "de" | "en" | "es" | "tr" | "ar" | "uk";

export interface LanguageInfo {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
  flag: string;
}

// RTL languages
export const RTL_LANGUAGES: SupportedLanguage[] = ["ar"];

export const isRtlLanguage = (lang: SupportedLanguage): boolean => {
  return RTL_LANGUAGES.includes(lang);
};

export const SUPPORTED_LANGUAGES: LanguageInfo[] = [
  { code: "de", name: "German", nativeName: "Deutsch", flag: "🇩🇪" },
  { code: "en", name: "English", nativeName: "English", flag: "🇬🇧" },
  { code: "es", name: "Spanish", nativeName: "Español", flag: "🇪🇸" },
  { code: "tr", name: "Turkish", nativeName: "Türkçe", flag: "🇹🇷" },
  { code: "ar", name: "Arabic", nativeName: "العربية", flag: "🇸🇦" },
  { code: "uk", name: "Ukrainian", nativeName: "Українська", flag: "🇺🇦" },
];

interface UseUserLanguageReturn {
  language: SupportedLanguage;
  isLoading: boolean;
  needsOnboarding: boolean;
  setLanguage: (lang: SupportedLanguage) => Promise<void>;
  getTranslatedContent: (contentKey: string, fallbackText: string) => Promise<string>;
}

export function useUserLanguage(): UseUserLanguageReturn {
  const [language, setLanguageState] = useState<SupportedLanguage>("de");
  const [isLoading, setIsLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  // Load user's language preference on mount
  useEffect(() => {
    const loadLanguagePreference = async () => {
      try {
        // Demo/Test: simulate first-time visitor without touching persisted account settings
        if (isFreshStartMode()) {
          const storedLang = localStorage.getItem("preferred_language") as SupportedLanguage | null;
          const hasSelectedLanguage = localStorage.getItem("language_onboarding_completed");
          const supportedCodes = SUPPORTED_LANGUAGES.map(l => l.code);

          if (hasSelectedLanguage && storedLang && supportedCodes.includes(storedLang)) {
            setLanguageState(storedLang);
            setNeedsOnboarding(false);
          } else {
            setNeedsOnboarding(true);
          }

          setIsLoading(false);
          return;
        }

        // Check localStorage first for non-logged-in users
        const storedLang = localStorage.getItem("preferred_language") as SupportedLanguage | null;
        const hasSelectedLanguage = localStorage.getItem("language_onboarding_completed");

        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          // Not logged in - check if they've completed language onboarding
          if (hasSelectedLanguage && storedLang) {
            const supportedCodes = SUPPORTED_LANGUAGES.map(l => l.code);
            if (supportedCodes.includes(storedLang)) {
              setLanguageState(storedLang);
            }
            setNeedsOnboarding(false);
          } else {
            // Show onboarding for first-time visitors
            setNeedsOnboarding(true);
          }
          setIsLoading(false);
          return;
        }

        setUserId(user.id);

        // Check if user has a language preference
        const { data: preference, error } = await supabase
          .from("user_language_preferences")
          .select("preferred_language")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) {
          console.error("Error loading language preference:", error);
          setIsLoading(false);
          return;
        }

        if (preference?.preferred_language) {
          setLanguageState(preference.preferred_language as SupportedLanguage);
          setNeedsOnboarding(false);
        } else {
          // User is logged in but has no language preference - show onboarding
          setNeedsOnboarding(true);
        }
      } catch (err) {
        console.error("Error in loadLanguagePreference:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadLanguagePreference();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        loadLanguagePreference();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Set user's language preference
  const setLanguage = useCallback(async (lang: SupportedLanguage) => {
    setLanguageState(lang);
    setNeedsOnboarding(false);
    
    // Always save to localStorage for persistence
    localStorage.setItem("preferred_language", lang);
    localStorage.setItem("language_onboarding_completed", "true");

    // Demo/Test mode: do not touch the logged-in user's persisted preference
    if (isFreshStartMode()) {
      return;
    }

    if (!userId) {
      // Not logged in - just update local state
      return;
    }

    try {
      // Upsert the language preference
      const { error } = await supabase
        .from("user_language_preferences")
        .upsert({
          user_id: userId,
          preferred_language: lang,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error("Error saving language preference:", error);
        throw error;
      }

      setNeedsOnboarding(false);
    } catch (err) {
      console.error("Error in setLanguage:", err);
      throw err;
    }
  }, [userId]);

  // Get translated content with fallback
  const getTranslatedContent = useCallback(async (contentKey: string, fallbackText: string): Promise<string> => {
    // If German is selected, return the fallback (original German text)
    if (language === "de") {
      return fallbackText;
    }

    try {
      const { data, error } = await supabase.rpc("get_translated_content", {
        p_content_key: contentKey,
        p_language: language,
      });

      if (error) {
        console.error("Error getting translated content:", error);
        return fallbackText;
      }

      return data || fallbackText;
    } catch (err) {
      console.error("Error in getTranslatedContent:", err);
      return fallbackText;
    }
  }, [language]);

  return {
    language,
    isLoading,
    needsOnboarding,
    setLanguage,
    getTranslatedContent,
  };
}

export default useUserLanguage;
